import date from "date-and-time";
import { Combobox } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useState, useMemo, useEffect } from "react";
import { ILead } from "../../models/Lead";
import { TemplateType } from "../../models/TemplateEditor";
import classNames from "../../utils/classNames";
import useMessages from "../../data/useMessages";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useCustomerDetails from "../../data/useCustomerDetails";
import useTemplateEditor from "../../data/useTemplateEditor";
import useNotification from "../notifications/useNotifications";
import { useTranslation } from "react-i18next";


function applyMergeTags(body: string, lead: ILead, companyName: string): string {
  const collectionDate = lead.vehicle?.scheduledCollectionOn
    ? date.format(new Date(lead.vehicle.scheduledCollectionOn), "HH:mm DD/MM/YYYY")
    : "";

  return body
    .replace(/\{\{ContactName\}\}/g, lead.contact?.fullname?.trim() ?? "")
    .replace(/\{\{CompanyName\}\}/g, companyName)
    .replace(/\{\{CollectionDateTime\}\}/g, collectionDate)
    .replace(/\{\{VehicleMake\}\}/g, lead.vehicle?.combinedMake ?? "")
    .replace(/\{\{VehicleModel\}\}/g, lead.vehicle?.modelRangeDescription ?? "")
    .replace(/\{\{VehicleRegistration\}\}/g, lead.vehicle?.vrm ?? "");
}


export default function SendText({ lead, setOpen }: { lead: ILead, setOpen: any }) {
  const { t } = useTranslation("leads");
  const { sendText } = useMessages();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Send text"));
  const { addNotification } = useNotification();
  const [selected, setSelected] = useState<any>({ id: undefined });
  const [telephone, setTelephone] = useState<string>(lead.contact?.telephone1 ?? "");
  const [content, setContent] = useState<string>("");

  useEffect(() => {
    setTelephone(lead.contact?.telephone1 ?? "");
  }, [lead.contact?.telephone1]);

  const { customer } = useCustomerDetails();
  const { templates } = useTemplateEditor();

  const textTemplates = useMemo(() => {
    const companyName = customer.data?.data.name ?? "";
    const allowedTitles = ["Enquiry Received", "Vehicle Collection"];
    return templates
      .filter((t) => t.type === TemplateType.Message && allowedTitles.includes(t.title))
      .map((t) => {
        const body = t.childTemplate?.body ?? t.body;
        return {
          id: t.id,
          title: t.childTemplate?.title ?? t.title,
          content: applyMergeTags(body, lead, companyName),
        };
      });
  }, [templates, customer.data, lead]);

  const handleSetSelected = (template: any) => {
    setSelected(template);
    setContent(template.content);
  }

  const handleSendText = () => {
    var text = {
      ContactId: lead.contact?.id,
      // Without this the text is stored against the contact but no lead, so the conversation
      // thread cannot say which vehicle it was about and it counts as unread on every lead.
      LeadId: lead.id,
      Recipient: telephone,
      Body: content,
    }

    sendText.mutate(text, {
      onSuccess: (res: any) => {
        // The API answers 200 with Success: false when the send is rejected - an invalid number,
        // no SMS credit, Twilio refusing it. Treating any 200 as sent told the user their text
        // had gone when nothing was ever sent.
        if (res?.success === false) {
          addNotification({
            variant: "error",
            primaryText: t("Text not sent"),
            secondaryText: res.message,
          });
          return;
        }

        setOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      }
    })
  }

  return (
    <Combobox>
      {({ activeOption }) => (
        <>
          <Combobox.Options
            as="div"
            static
            className="flex divide-y divide-gray-100 flex-col sm:flex-row sm:divide-x h-full"
          >
            <div className="h-full min-w-0 h-80 flex-auto scroll-py-4 overflow-y-auto px-6 py-4">
              <div className="-mx-2 text-sm text-gray-700">
                {textTemplates.map(
                  (template) => (
                    <Combobox.Option
                      as="div"
                      key={template.id}
                      value={template}
                      onClick={() => handleSetSelected(template)}
                      className={({ active }) =>
                        classNames(active || selected.id === template.id ? "bg-gray-100 text-gray-900" : "",
                          "flex cursor-pointer select-none items-center rounded-md p-2"
                        )
                      }
                    >
                      {({ active }) => (
                        <>
                          <span className="ml-3 flex-auto truncate">
                            {template.title}
                          </span>
                          {Boolean(active || selected.id === template.id) && (
                            <ChevronRightIcon
                              className="ml-3 h-5 w-5 flex-none text-gray-400"
                              aria-hidden="true"
                            />
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  )
                )}
              </div>
            </div>

            {selected.id ? (
              <div className="h-full w-full flex-none grow flex-col divide-y divide-gray-100 overflow-y-auto flex sm:w-1/2">
                <div className="p-6">
                  <div className="mb-4">
                    <label>{t("Telephone Number:")}</label>
                    <input value={telephone} onChange={((e) => setTelephone(e.target.value))} type="text" className="text-sm w-full rounded-md border-gray-600" />
                  </div>

                  <div>
                    <label>{t("Content:")}</label>
                  </div>
                  <textarea style={{ resize: "none" }} value={content} onChange={((e) => setContent(e.target.value))} rows={18} className="text-sm w-full rounded-md border-gray-600" />
                </div>


                <div className="flex flex-auto flex-col justify-between p-6">
                  <button
                    onClick={() => handleSendText()}
                    type="button"
                    className="w-full rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    {t("Send")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="h-80 w-full sm:h-96 sm:w-1/2"></div>
            )}
          </Combobox.Options>
        </>
      )}
    </Combobox>
  )
}